<script setup lang="ts">
// import { AxiosError } from 'axios'
import { AxiosError } from 'axios'
import { NovaModalDialog } from '#components'
import {
  type BooleanYn,
  type KpopFestivalFormPayload,
  KpopFestivalId,
  type MemberListItem,
} from '@store'
import { useCheckEmail } from '@composables/useCheckEmail'
import { ROUTES } from '@configs'

const memberRefs = ref<Array<{ $el: HTMLElement } | null>>([])
const introduceLinkInput = ref<{ $el: HTMLElement } | null>(null)
const teamNameInput = ref<{ $el: HTMLElement } | null>(null)

const layoutStore = useLayoutStore()
const userStore = useUserStore()
const kpopFestivalStore = useKpopFestivalStore()
const { t, messages, locale } = useI18n()
const { show: modalShow, hide: modalHide } = useModal()
const { gtEvent } = useGoogleTag()

// 레이아웃 설정
definePageMeta({
  layout: 'one-column',
  middleware: ['apply-kpop-festival-guard'],
})

// seo 설정
useSetSEO()

// 멤버 타입
export type member = {
  name: string
  email: string
  phone: string
  age: number
  wallet: string
  reprsntAt: BooleanYn
  expsrOrdr: number
  simpleMode: boolean
  err: {
    name: string | null
    email: string | null
    phone: string | null
    age: string | null
  }
}
// const isComposing = ref(false)
const isFocused = ref(false)

// 멤버 입력 사이즈 조절
const handleSimpleMode = (index: number) => {
  memberList.value[index].simpleMode = !memberList.value[index].simpleMode
}

// 멤버 추가
const handleOnDummy = () => {
  memberList.value = [
    ...memberList.value,
    { ...initMemberList, expsrOrdr: memberList.value.length },
  ]
}

// 멤버 입력 삭제
const deleteMemberField = (index: number) => {
  modalShow({
    component: NovaModalDialog,
    bind: {
      name: modalsName.MODAL_DIALOG,
      btns: [
        {
          label: t('applyKpopCoverDance.message.deleteMember.btn.cancel'),
          theme: 'secondary-gray-200',
          size: 40,
          onClick: async () => {
            await modalHide(modalsName.MODAL_DIALOG)
          },
        },
        {
          label: t('applyKpopCoverDance.message.deleteMember.btn.confirm'),
          theme: 'primary-blue-dark',
          size: 40,
          onClick: async () => {
            await modalHide(modalsName.MODAL_DIALOG)
            memberList.value.splice(index, 1)
          },
        },
      ],
      contentsTextAlign: 'center',
    },
    slots: {
      title: t('applyKpopCoverDance.message.deleteMember.title'),
      content: t('applyKpopCoverDance.message.deleteMember.contents'),
    },
  })
}

// 전화번호 양식 자동 완성
const makePhoneNum = (event: any, index: number) => {
  let input = event.target.value.replace(/\D/g, '') // 숫자 외의 문자 제거
  if (input.length > 3 && input.length <= 7) {
    input = input.slice(0, 3) + '-' + input.slice(3)
  } else if (input.length > 6) {
    input =
      input.slice(0, 3) + '-' + input.slice(3, 6) + '-' + input.slice(6, 10)
  }
  memberList.value[index].phone = input
}
// 지갑 주소 양식 자동 완성
const makeWalletAdd = (event: any, index: number) => {
  let inputValue = event.target.value.replace(/[^a-zA-Z0-9]/g, '') // 영어와 숫자만 허용
  if (inputValue.length > 42) {
    inputValue = inputValue.slice(0, 42) // 최대 42자 제한
  }
  memberList.value[index].wallet = inputValue
}

// 나이 양식 자동 완성
const makeAge = (event: any, index: number) => {
  let inputValue = event.target.value.replace(/[^0-9]/g, '') // 숫자만 허용
  if (inputValue.length > 2) {
    inputValue = inputValue.slice(0, 2) // 최대 2자 제한
  }
  memberList.value[index].age = inputValue
}
// 멤버 추가
const initMemberList: member = {
  name: '',
  email: '',
  phone: '',
  age: 0,
  wallet: '',
  reprsntAt: 'N',
  expsrOrdr: -1,
  simpleMode: false,
  err: {
    name: null,
    email: null,
    phone: null,
    age: null,
  },
}

// 멤버 모델
const memberList = ref<member[]>([
  {
    name: '',
    email: '',
    phone: '',
    age: 0,
    wallet: '',
    reprsntAt: 'Y',
    expsrOrdr: 0,
    simpleMode: false,
    err: {
      name: null,
      email: null,
      phone: null,
      age: null,
    },
  },
])

const memberListValidator = () => {
  let firstErrorIndex = -1 // 첫 번째 에러 인덱스를 추적하기 위한 변수
  let firstErrorInPutType = '' // 첫 번째 에러 인풋 명

  memberList.value.forEach((member, index) => {
    // member 객체를 깊은 복사하여 수정 (예: JSON 방법으로)
    const memberCopy = JSON.parse(JSON.stringify(member))
    // 필드 값이 비어 있을 경우 에러 메시지 추가 및 첫 번째 에러 인덱스 설정
    if (!memberCopy.name) {
      memberCopy.err.name = t('applyKpopCoverDance.message.err')
      if (firstErrorIndex === -1) firstErrorIndex = index
      if (!firstErrorInPutType) firstErrorInPutType = 'name'
    } else {
      memberCopy.err.name = null
    }
    if (!memberCopy.email) {
      memberCopy.err.email = t('applyKpopCoverDance.message.err')
      if (firstErrorIndex === -1) firstErrorIndex = index
      if (!firstErrorInPutType) firstErrorInPutType = 'email'
    } else if (!useCheckEmail(memberCopy.email)) {
      memberCopy.err.email = t('applyKpopCoverDance.message.errEmail')
      if (firstErrorIndex === -1) firstErrorIndex = index
      if (!firstErrorInPutType) firstErrorInPutType = 'email'
    } else {
      memberCopy.err.email = null
    }
    if (!memberCopy.phone) {
      memberCopy.err.phone = t('applyKpopCoverDance.message.err')
      if (firstErrorIndex === -1) firstErrorIndex = index
      if (!firstErrorInPutType) firstErrorInPutType = 'phone'
    } else {
      memberCopy.err.phone = null
    }
    if (memberCopy.age <= 0) {
      memberCopy.err.age = t('applyKpopCoverDance.message.err')
      if (firstErrorIndex === -1) firstErrorIndex = index
      if (!firstErrorInPutType) firstErrorInPutType = 'age'
    } else {
      memberCopy.err.age = null
    }
    memberList.value[index] = memberCopy
  })
  if (firstErrorIndex >= 0) {
    const findFirstErrEl = document.getElementById(
      `member-${firstErrorInPutType}-${firstErrorIndex}`
    )
    const input = findFirstErrEl?.querySelector('input')
    input?.focus()
    // 검사 결과 값 리턴
    return false
  }
  // 검사 결과 값 리턴
  return true
}

// 비디오 링크 첨부 디스크립션
const desc = computed(() =>
  Object.keys(
    (messages.value[locale.value].applyKpopCoverDance as any).introduce.video
      .descriptions
  ).map((key) => t(`applyKpopCoverDance.introduce.video.descriptions.${key}`))
)

// 팀 정보 모델
const teamName = reactive({
  value: '',
  placeHolder: t('applyKpopCoverDance.team.placeHolder'),
  err: '',
  validator: (showErrorMsg = true) => {
    const isTeam = memberList.value.length > 1
    const isNotEmpty = teamName.value.length > 0
    if (showErrorMsg) {
      teamName.err =
        isNotEmpty && isTeam ? '' : t('applyKpopCoverDance.message.err')
      if (!isNotEmpty && isTeam && !isFocused.value) {
        const inputElement = teamNameInput.value?.$el.querySelector('input')
        inputElement?.focus()
        isFocused.value = true
      }
    } else teamName.err = ''
    if (isTeam) {
      return isNotEmpty
    }
    return true
  },
})

// 팀 대표자 계산
const representativeItem = computed(() => {
  return memberList.value
    .filter((member) => member.name) // name이 빈 문자열이 아닌 항목만 포함
    .map((member, index) => ({
      label: member.name,
      value: index,
    }))
})

// 팀 대표자 모델
const representativeItems = reactive({
  value: 0,
  items: representativeItem,
  err: '',
  onChangeValue: (value: number) => {
    representativeItems.value = value
    memberList.value = memberList.value.map((member, index) => ({
      ...member,
      reprsntAt: index === value ? 'Y' : 'N',
    }))
  },
})

// 소개 링크 모델
const introduceLink = reactive({
  value: '',
  placeHolder: t('applyKpopCoverDance.introduce.video.placeHolder'),
  err: '',
  validator: (showErrorMsg = true) => {
    // 입력 여부
    const urlPattern =
      /^https?:\/\/(www\.)?(tiktok\.com|youtube\.com|youtu\.be|instagram\.com)\//
    const isValidUrl = urlPattern.test(introduceLink.value)
    const isNotEmpty = introduceLink.value.length > 0
    const isValid = isNotEmpty && isValidUrl
    if (showErrorMsg) {
      if (!isNotEmpty) {
        introduceLink.err = t('applyKpopCoverDance.message.err')
      } else if (!isValidUrl) {
        introduceLink.err = t('applyKpopCoverDance.message.invalidUrl') // 유효하지 않은 URL에 대한 오류 메시지
      } else {
        introduceLink.err = ''
      }

      if (!isValid && !isFocused.value) {
        const inputElement =
          introduceLinkInput.value?.$el.querySelector('input')
        inputElement?.focus()
        inputElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        })
        isFocused.value = true
      }
    } else {
      introduceLink.err = ''
    }

    return isValid
  },
})

// 지원 동기 모델
const motivate = reactive({
  value: '',
  placeHolder: '',
})

// 약관 동의 모델
const kpopFestivalTerms = reactive({
  value: [],
  terms: kpopFestivalStore.kpopFestivalTerms,
  validator: (showErrMsg = true) => {
    const isValid =
      kpopFestivalTerms.terms?.length === kpopFestivalTerms.value.length

    if (!isValid && showErrMsg) {
      modalShow(
        {
          component: NovaModalDialog,
          bind: {
            name: modalsName.MODAL_DIALOG,
            btns: [
              {
                label: t('applyKpopCoverDance.terms.confirm'),
                theme: 'primary-blue-dark',
                size: 32,
                onClick: async () => {
                  await modalHide(modalsName.MODAL_DIALOG, false)
                },
              },
            ],
          },
          slots: {
            title: t('applyKpopCoverDance.terms.err.title'),
            content: t('applyKpopCoverDance.terms.err.content'),
          },
        },
        '',
        '',
        false
      )
    }
    return isValid
  },
})
const transformMemberList = (
  members: typeof memberList.value
): MemberListItem[] => {
  return members.map((member) => ({
    age: member.age,
    email: member.email,
    mobileNumber: member.phone.replace(/-/g, ''), // `phone`을 `mobileNumber`로 매핑
    name: member.name,
    reprsntAt: members.length === 1 ? 'Y' : member.reprsntAt,
    applcntCxwaletAdres: member.wallet,
  }))
}

const onSubmitLoading = ref(false)

const payload = reactive<KpopFestivalFormPayload>({
  agreeFstvlApprtcStplatSnList: [], // 동의 약관 일련번호 목록
  fstvlSectCode: 'P', // 참가 부문 코드
  introductionVideoUrl: '', // 참가 소개 영상 URL
  memberList: [], // 참가 멤버 리스트
  motivationForApplication: motivate.value, // 지원 동기
  teamName: teamName.value, // 팀 명
})
const onSubmit = async () => {
  try {
    onSubmitLoading.value = true
    isFocused.value = false
    let allIsValid = true
    const validators = [
      kpopFestivalTerms.validator,
      teamName.validator,
      introduceLink.validator,
      memberListValidator,
    ]
    // 유효성 검사
    validators.forEach((validator) => {
      const isValid = validator()
      if (!isValid) allIsValid = false
    })
    if (allIsValid) {
      Object.assign(payload, {
        agreeFstvlApprtcStplatSnList: kpopFestivalTerms.value, // 동의 약관 일련번호 목록
        fstvlSectCode: memberList.value.length > 1 ? 'G' : 'P', // 참가 부문 코드
        introductionVideoUrl: introduceLink.value, // 참가 소개 영상 URL
        memberList: transformMemberList(memberList.value), // 참가 멤버 리스트
        motivationForApplication: motivate.value, // 지원 동기
        teamName: memberList.value.length > 1 ? teamName.value : '', // 팀 명
      })
      await modalShow({
        component: NovaModalDialog,
        bind: {
          name: modalsName.MODAL_DIALOG,
          btns: [
            {
              label: t('applyKpopCoverDance.message.confirmApply.btn.cancel'),
              theme: 'secondary-gray-200',
              size: 40,
              onClick: async () => {
                await modalHide(modalsName.MODAL_DIALOG)
              },
            },
            {
              label: t('applyKpopCoverDance.message.confirmApply.btn.confirm'),
              theme: 'primary-blue-dark',
              size: 40,
              onClick: async () => {
                try {
                  await kpopFestivalStore.reqKpopFestivalApply(payload)
                  console.log(payload)
                  await gtEvent('clickEvent', {
                    eventCategory: '클릭',
                    eventAction: '커버댄스 이벤트 2차 신청',
                    eventLabel: t('applyKpopCoverDance.applyBtn'),
                    eventSlot: '',
                    eventI18nAddr: t('applyKpopCoverDance.applyBtn'),
                    eventComponent: 'Button',
                    introductionVideoUrl: introduceLink.value,
                  })
                  await modalHide(modalsName.MODAL_DIALOG)
                  await useNavigations({
                    url: useRoutePathIdChange(ROUTES.BOARD_EVENT_DETAIL.path, {
                      id: KpopFestivalId.SECOND,
                    }),
                  })
                } catch {
                  await useToast(t('commonError.network.message'))
                  await modalHide(modalsName.MODAL_DIALOG)
                }
              },
            },
          ],
          contentsTextAlign: 'center',
        },
        slots: {
          title: t('applyKpopCoverDance.message.confirmApply.title'),
          content: t('applyKpopCoverDance.message.confirmApply.contents'),
        },
      })
    }
  } catch (err) {
    if (err instanceof AxiosError) {
      await useToast(t('commonError.network.message'))
    }
    await modalHide(modalsName.MODAL_DIALOG)
  } finally {
    onSubmitLoading.value = false
  }
}

onBeforeMount(async () => {
  const now = new Date() // 현재 시간
  const endDate = new Date('2024-12-21') // 마감 일자
  if (now > endDate) {
    await modalShow({
      component: NovaModalDialog,
      bind: {
        name: modalsName.MODAL_DIALOG,
        btns: [
          {
            label: t('applyKpopCoverDance.message.deleteMember.btn.confirm'),
            theme: 'primary-blue-dark',
            size: 40,
            onClick: async () => {
              await modalHide(modalsName.MODAL_DIALOG)
              await useNavigations({
                url: useRoutePathIdChange(ROUTES.BOARD_EVENT_DETAIL.path, {
                  id: KpopFestivalId.SECOND,
                }),
              })
            },
          },
        ],
        contentsTextAlign: 'center',
      },
      slots: {
        title: t('applyKpopCoverDance.message.deleteMember.title'),
        content: t('applyKpopCoverDance.message.applicationCloses.content'),
      },
    })
    return
  }
  await kpopFestivalStore.fetchKpopFestivalTerms()
  kpopFestivalTerms.terms = kpopFestivalStore.kpopFestivalTerms
})

onMounted(() => {
  layoutStore.updateMainBgWhite(true)
  layoutStore.updateGnbMobileHide(true)
  if (userStore.user) {
    memberList.value[0].name = userStore.user.userNm || ''
    memberList.value[0].email = userStore.user.email || ''
    memberList.value[0].phone = userStore.user.mobile || ''
    memberList.value[0].age = useAgeCalculate(String(userStore.user.birth)) || 0
  }
})

onBeforeUnmount(() => {
  layoutStore.updateMainBgWhite(false)
  layoutStore.updateGnbMobileHide(false)
})
</script>

<template>
  <div class="page-apply-rent">
    <NovaHeaderMobile :show-back-action="true" :title="'Application Form'" />

    <div class="inner">
      <section class="section">
        <!-- 지원자 정보 입력 -->
        <div class="form-field-wrap">
          <div class="section-title-wrap">
            <h4
              v-dompurify-html="t('applyKpopCoverDance.entry')"
              class="title"
            />
          </div>
          <!-- member 입력 필드 -->
          <div
            v-for="(item, index) in memberList"
            ref="memberRefs"
            :key="index"
            class="member-info-wrap"
          >
            <div v-if="index > 0" class="member-info-top">
              <div class="member-open-wrap">
                <p class="tit">
                  {{ t('applyKpopCoverDance.member.top') }} {{ index }}
                </p>
                <NovaIcon
                  :class="['btn-open', { on: item.simpleMode }]"
                  :icon="{ type: 'outline', name: 'chev-compact-top' }"
                  :size="24"
                  @click="handleSimpleMode(index)"
                />
              </div>
              <div class="btn-delete-wrap">
                <NovaIcon
                  class="btn-delete"
                  :icon="{ type: 'outline', name: 'close-extend' }"
                  :size="18"
                  @click="deleteMemberField(index)"
                />
              </div>
            </div>
            <div v-if="!item.simpleMode" class="item-box">
              <div class="form-field-wrap jc-sb">
                <!-- Name 필드 -->
                <div class="form-item">
                  <div
                    v-dompurify-html="
                      t('applyKpopCoverDance.member.name.label')
                    "
                    class="label required"
                  />
                  <div class="field">
                    <NovaInput
                      :id="`member-name-${index}`"
                      v-model="item.name"
                      :theme="'search'"
                      :placeholder="
                        t('applyKpopCoverDance.member.name.placeholder')
                      "
                      @on-blur="item.err.name ? memberListValidator() : ''"
                    />
                    <div class="text-bottom">
                      <p v-if="item.err.name" class="error-message">
                        {{ item.err.name }}
                      </p>
                    </div>
                  </div>
                </div>
                <!-- Email 필드 -->
                <div class="form-item">
                  <div
                    v-dompurify-html="
                      t('applyKpopCoverDance.member.email.label')
                    "
                    class="label required"
                  />
                  <div class="field">
                    <NovaInput
                      :id="`member-email-${index}`"
                      v-model="item.email"
                      :theme="'search'"
                      :placeholder="
                        t('applyKpopCoverDance.member.email.placeholder')
                      "
                      @on-blur="item.err.email ? memberListValidator() : ''"
                    />
                    <div class="text-bottom">
                      <p v-if="item.err.email" class="error-message">
                        {{ item.err.email }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-field-wrap jc-sb">
                <!-- Mobile Number 필드 -->
                <div class="form-item">
                  <div
                    v-dompurify-html="
                      t('applyKpopCoverDance.member.phone.label')
                    "
                    class="label required"
                  />
                  <div class="field">
                    <NovaInput
                      :id="`member-phone-${index}`"
                      v-model="item.phone"
                      :theme="'search'"
                      :placeholder="
                        t('applyKpopCoverDance.member.phone.placeholder')
                      "
                      @input="(event: InputEvent) => makePhoneNum(event, index)"
                      @on-blur="item.err.phone ? memberListValidator() : ''"
                    />
                    <div class="text-bottom">
                      <p v-if="item.err.phone" class="error-message">
                        {{ item.err.phone }}
                      </p>
                    </div>
                  </div>
                </div>
                <!-- 나이 필드 -->
                <div class="form-item">
                  <div
                    v-dompurify-html="t('applyKpopCoverDance.member.age.label')"
                    class="label required"
                  />
                  <div class="field">
                    <NovaInput
                      :id="`member-age-${index}`"
                      v-model="item.age"
                      :theme="'search'"
                      :placeholder="
                        t('applyKpopCoverDance.member.age.placeholder')
                      "
                      @input="(event: InputEvent) => makeAge(event, index)"
                      @on-blur="item.err.age ? memberListValidator() : ''"
                    />
                    <div class="text-bottom">
                      <p
                        v-dompurify-html="
                          t('applyKpopCoverDance.member.age.message')
                        "
                        class="message"
                      />
                      <p v-if="item.err.age" class="error-message">
                        {{ item.err.age }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="form-field-wrap jc-sb">
                <!-- 지갑 주소 필드 -->
                <div class="form-item">
                  <div
                    v-dompurify-html="
                      t('applyKpopCoverDance.member.wallet.label')
                    "
                    class="label"
                  />
                  <div class="field">
                    <NovaInput
                      :id="`member-wallet-${index}`"
                      v-model="item.wallet"
                      :type="'email'"
                      :theme="'search'"
                      :max-length="42"
                      @input="(event: InputEvent) => makeWalletAdd(event, index)"
                    />
                    <div class="text-bottom">
                      <p
                        v-dompurify-html="
                          t('applyKpopCoverDance.member.wallet.message')
                        "
                        class="message"
                      />
                      <p
                        v-dompurify-html="
                          t('applyKpopCoverDance.member.wallet.message2')
                        "
                        class="message"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <NovaButtonText
            :label="t('applyKpopCoverDance.addMember')"
            :theme="'white-border-gray'"
            @click="handleOnDummy()"
          />
          <!-- 멤버가 2명 이상 일때 노출  -->
          <!-- 대표 입력 필드 기본 처음 작성자 -->
          <div v-if="memberList.length > 1" class="form-field-wrap jc-sb">
            <!-- Team Name 필드 -->
            <div>
              <div
                v-dompurify-html="t('applyKpopCoverDance.team.label')"
                class="label required"
              />
              <div class="field">
                <NovaInput
                  ref="teamNameInput"
                  v-model="teamName.value"
                  :placeholder="t('applyKpopCoverDance.team.placeHolder')"
                  :theme="'search'"
                  @on-blur="teamName.validator()"
                />
                <div class="text-bottom">
                  <p v-if="teamName.err" class="error-message">
                    {{ teamName.err }}
                  </p>
                </div>
              </div>
            </div>
            <!-- 기본 처음 작성자가 들어가고 멤버 추가시 추가 됨 -->
            <div>
              <div
                v-dompurify-html="t('applyKpopCoverDance.representative.label')"
                class="label required"
              />
              <div class="field">
                <NovaDropdown
                  :menus="representativeItems.items"
                  :active-value="representativeItems.value"
                  @update="representativeItems.onChangeValue"
                />
                <div class="text-bottom">
                  <p v-if="representativeItems.err" class="error-message">
                    {{ representativeItems.err }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section class="section">
        <!-- 소개 입력 -->
        <div class="form-field-wrap">
          <div class="section-title-wrap">
            <h4
              v-dompurify-html="t('applyKpopCoverDance.introduce.label')"
              class="title"
            ></h4>
          </div>
          <!-- 소개영상 url 필드 -->
          <div class="form-field-wrap full-width">
            <div>
              <div
                v-dompurify-html="
                  t('applyKpopCoverDance.introduce.video.label')
                "
                class="label required"
              />
              <div class="field">
                <NovaInput
                  ref="introduceLinkInput"
                  v-model="introduceLink.value"
                  :theme="'search'"
                  :placeholder="introduceLink.placeHolder"
                  @on-blur="introduceLink.validator()"
                />
                <div class="text-bottom">
                  <p v-if="introduceLink.err" class="error-message">
                    {{ introduceLink.err }}
                  </p>
                </div>
                <NovaDescriptions :descriptions="desc" />
              </div>
            </div>
          </div>

          <!-- 지원동기 필드 -->
          <div class="form-field-wrap full-width">
            <div>
              <div
                v-dompurify-html="
                  t('applyKpopCoverDance.introduce.motivation.label')
                "
                class="label"
              />
              <div class="field">
                <NovaTextarea
                  v-model="motivate.value"
                  :theme="'search'"
                  :max-length="1000"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- 5. 약관 동의 -->
      <NovaBoxKpopFestivalTerms
        v-model="kpopFestivalTerms.value"
        :terms="kpopFestivalTerms.terms"
      />
      <!-- 참가 신청 버튼-->
      <div class="go-next-wrap">
        <ClientOnly>
          <section class="apply">
            <NovaButtonText
              :label="t('applyKpopCoverDance.applyBtn')"
              :width="335"
              :size="44"
              :full-width="false"
              :theme="`primary-blue-dark`"
              @click="onSubmit()"
            />
          </section>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-apply-rent {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding-bottom: 24px;

  :deep(.mobile-header) {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    max-width: 1200px + 12px + 12px;
    height: auto;
    padding: 40px 0;
    text-align: center;
    border-bottom: none;
    @include mobile {
      max-width: 100%;
      padding: 32px 0;
    }

    .navigation-button {
      position: absolute;
      left: 12px;
    }
  }

  > .inner {
    width: 100%;
    max-width: 1200px + 12px + 12px;
    padding: 20px 12px 100px;
    background-color: #fff;
    @include mobile {
      max-width: 100%;
      padding: 0 16px 24px;
    }

    .section {
      display: flex;
      flex-direction: column;
      gap: 100px;
      margin-bottom: 35px;
      @include mobile {
        gap: 50px;
      }
      .section-title-wrap {
        width: 100%;
        padding-bottom: 16px;
        //border-bottom: 2px solid $color-neutral-light-2;
        @include mobile {
          padding-bottom: 10px;
        }

        > .title {
          @include text-style($text-heading2-bold);
          color: $color-black;
          @include mobile {
            @include text-style($text-heading3-bold);
          }
        }
      }
      .member-info-wrap {
        display: flex;
        flex-direction: column;
        gap: 24px;
        padding: 30px;
        border-radius: $radius-10;
        box-shadow: 0 0 8px 5px rgba(0, 0, 0, 0.04);
        @include mobile {
          gap: 16px;
          padding: 20px;
          border-radius: 15px;
        }
        .member-info-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          .member-open-wrap {
            display: flex;
            justify-content: flex-start;
            gap: 8px;
            align-items: center;
            .tit {
              @include text-style($text-display-bold);
            }
            .btn-open {
              cursor: pointer;
              transition: all 0.3s ease;
              &.on {
                transform: rotate(180deg);
              }
            }
          }
          .btn-delete-wrap {
            cursor: pointer;
          }
        }
      }
      .item-box {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .form-field-wrap {
        display: flex;
        flex-direction: column;
        gap: 24px;
        @include mobile {
          gap: 16px;
        }

        &.jc-sb {
          display: flex;
          justify-content: space-between;
          flex-direction: row;
          @include mobile {
            flex-direction: column;
          }
          > div {
            width: calc(50% - 20px);
            &.form-item {
              min-height: 110px;
              @include mobile {
                min-height: 80px;
              }
            }
            @include mobile {
              width: 100%;
            }
          }

          .label {
            position: relative;
            display: inline-block;
            padding-bottom: 8px;
            @include text-style($text-custom-18-bold);
            @include mobile {
              @include text-style($text-body-14-bold);
            }
            &.required:after {
              position: absolute;
              top: 8px;
              right: -8px;
              display: inline-block;
              content: '';
              width: 5px;
              height: 5px;
              border-radius: 3px;
              background-color: $color-warning;
            }
          }
        }
        &.full-width {
          display: flex;
          flex-direction: column;
          gap: 0;
          @include mobile {
            gap: 24px;
          }
          > div {
            width: 100%;
          }
          .label {
            position: relative;
            display: inline-block;
            padding-bottom: 8px;
            @include text-style($text-custom-18-bold);
            @include mobile {
              @include text-style($text-body-14-bold);
            }
            &.required:after {
              position: absolute;
              top: 8px;
              right: -8px;
              display: inline-block;
              content: '';
              width: 5px;
              height: 5px;
              border-radius: 3px;
              background-color: $color-warning;
            }
          }
        }
        .text-bottom {
          display: flex;
          justify-content: space-between;
          flex-direction: column;
          @include tablet {
            flex-direction: column;
          }
          .message {
            @include text-style($text-body-14-medium);
            padding-top: 5px;
            color: $color-text-10;

            @include mobile {
              padding-top: 5px;
            }
          }
        }
        .descriptions {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding-top: 20px;
          @include mobile {
            padding-top: 10px;
          }
          :deep(p) {
            @include text-style($text-custom-15-medium);
            @include mobile {
              @include text-style($text-body-14-medium);
            }
          }
        }
      }
      .error-message {
        display: flex;
        align-items: center;
        gap: 6px;
        padding-top: 5px;
        @include text-style($text-body-14-medium);
        color: $color-bg-custom-12;

        &:before {
          @include error-icon;
        }
      }
    }
  }
  .go-next-wrap {
    display: flex;
    justify-content: center;
    .apply {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      @include mobile {
        .btn-44 {
          width: 100% !important;
          @include text-style($text-display-medium);
        }
      }
    }
  }
}
</style>
